import { CheckCircleOutlined } from "@ant-design/icons";
import { EAffiliateTransactionTrackingMethod } from "@devowl-wp/api-real-commerce";
import { EAffiliateTransactionType } from "@devowl-wp/api-real-commerce";
import { Empty, Row, Spin, Table, Tag, Tooltip, Typography, Col } from "antd";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { AccountAffiliateTransactionsChart } from ".";
import { DateRangePicker } from "../../../../components";
import { useStores } from "../../../../store";
import { formatNumber, __ } from "../../../../utils";
import moment from "moment";
import { useAvailableDateRangesForFilter } from "../../../../hooks";
const {
  Column
} = Table;
const {
  Paragraph
} = Typography;
const AccountAffiliateTransactionsTable = observer(() => {
  const {
    accountStore: {
      session: {
        affiliate
      }
    }
  } = useStores();
  const {
    busyTransactions,
    transactions,
    statistics,
    transactionFilters: {
      dates: currentDates
    }
  } = affiliate;
  const showChart = statistics.length > 0 && statistics.some(_ref => {
    let {
      value
    } = _ref;
    return !!value;
  });
  const {
    lang
  } = document.documentElement;

  // Provide preset ranges to the user
  const availableRanges = useAvailableDateRangesForFilter();
  useEffect(() => {
    affiliate.fetchTransactions();
  }, []);
  return /*#__PURE__*/React.createElement(Spin, {
    spinning: busyTransactions
  }, /*#__PURE__*/React.createElement(Row, {
    justify: "end",
    style: {
      marginBottom: 15
    }
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("label", null, __("Period"), ":\xA0", /*#__PURE__*/React.createElement(DateRangePicker, {
    disabledDate: d => !d || d.isAfter(moment()),
    value: /* Ant design relies on moment 2.27 and typings are currently incompatible with other moment typings */currentDates,
    ranges: availableRanges,
    onChange: dates => {
      affiliate.applyTransactionDates(dates || [undefined, undefined]);
      affiliate.fetchTransactions();
    }
  })))), showChart && /*#__PURE__*/React.createElement(AccountAffiliateTransactionsChart, null), /*#__PURE__*/React.createElement(Table, {
    style: {
      marginTop: showChart ? 15 : 0
    },
    bordered: true,
    locale: {
      emptyText: /*#__PURE__*/React.createElement(Empty, {
        description: __("There are no transactions in the selected period.")
      })
    },
    dataSource: Array.from(transactions.values()),
    rowKey: row => row.id,
    size: "small",
    pagination: {
      showSizeChanger: false
    }
  }, /*#__PURE__*/React.createElement(Column, {
    title: __("Product / Plan"),
    dataIndex: "product.id",
    key: "product",
    render: (_, _ref2) => {
      let {
        type,
        trackingMethod,
        coupon,
        product: {
          name
        },
        productPlan: {
          description
        }
      } = _ref2;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", null, name), type === EAffiliateTransactionType.Refund && /*#__PURE__*/React.createElement(Tag, {
        color: "error",
        style: {
          marginLeft: 5
        }
      }, __("Refund")), !!coupon && /*#__PURE__*/React.createElement(Tooltip, {
        title: __('This transaction was assigned to you because the buyer used the coupon code "%s".', coupon.code)
      }, /*#__PURE__*/React.createElement(Tag, {
        color: "blue",
        style: {
          marginLeft: 5
        }
      }, __("Coupon"))), trackingMethod === EAffiliateTransactionTrackingMethod.Support && /*#__PURE__*/React.createElement(Tooltip, {
        title: __("This transaction was subsequently assigned by a supporter.")
      }, /*#__PURE__*/React.createElement(Tag, {
        color: "blue",
        style: {
          marginLeft: 5
        }
      }, __("Support"))), /*#__PURE__*/React.createElement("br", null), description);
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Status"),
    dataIndex: "status",
    key: "status",
    width: 120,
    render: (_, _ref3) => {
      let {
        payout
      } = _ref3;
      return payout ? /*#__PURE__*/React.createElement(Tooltip, {
        title: __("Received in payout #%s at %s", payout.id, new Date(payout.createdAt).toLocaleDateString(lang))
      }, /*#__PURE__*/React.createElement(CheckCircleOutlined, {
        style: {
          color: "#32a771"
        }
      }), "\xA0", __("Paid")) : /*#__PURE__*/React.createElement(Tooltip, {
        title: __("The transaction was tracked and accepted. Commission will be paid to you on the 15th of the month following the month in which you earned it, if you reach the payout threshold.")
      }, /*#__PURE__*/React.createElement(CheckCircleOutlined, {
        style: {
          color: "#569ae1"
        }
      }), "\xA0", __("Accepted"));
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Date"),
    dataIndex: "date",
    key: "date",
    render: (_, _ref4) => {
      let {
        createdAt
      } = _ref4;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", {
        style: {
          fontSize: 15
        }
      }, new Date(createdAt).toLocaleDateString(lang)), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
        style: {
          opacity: 0.8,
          fontSize: 13
        }
      }, new Date(createdAt).toLocaleTimeString(lang)));
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: __("Source"),
    dataIndex: "source",
    key: "source",
    width: 200,
    render: (_, _ref5) => {
      let {
        linkView
      } = _ref5;
      if (!(linkView !== null && linkView !== void 0 && linkView.referer)) {
        return /*#__PURE__*/React.createElement("i", {
          style: {
            opacity: 0.7
          }
        }, __("not specified"));
      }
      const {
        hostname,
        pathname
      } = new URL(linkView.referer);
      return /*#__PURE__*/React.createElement(Tooltip, {
        title: linkView.referer
      }, /*#__PURE__*/React.createElement("a", {
        href: linkView.referer,
        target: "_blank",
        rel: "noreferrer"
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 15
        }
      }, hostname), /*#__PURE__*/React.createElement(Paragraph, {
        ellipsis: {
          rows: 1
        },
        style: {
          marginBottom: 0
        }
      }, pathname.length > 1 && /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 13
        }
      }, "\u2514 ", pathname))));
    }
  }), /*#__PURE__*/React.createElement(Column, {
    title: /*#__PURE__*/React.createElement(React.Fragment, null, __("Net commission"), /*#__PURE__*/React.createElement("br", null), "/ ", __("order value")),
    dataIndex: "netCommissionValue",
    key: "netCommissionValue",
    align: "right",
    render: (_, _ref6) => {
      let {
        type,
        netCommissionValue,
        netTransactionValue
      } = _ref6;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", {
        style: {
          fontSize: 15,
          color: type === EAffiliateTransactionType.Refund ? "darkred" : "#3f8600"
        }
      }, type === EAffiliateTransactionType.Refund ? "-" : "+", "\xA0", formatNumber(netCommissionValue, 2), " EUR"), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
        style: {
          opacity: 0.8,
          fontSize: 13
        }
      }, "/\xA0", formatNumber(netTransactionValue, 2), " EUR"));
    }
  })));
});
export { AccountAffiliateTransactionsTable };